import type { CSSProperties } from 'react';
import { Megaphone } from 'lucide-react';

export type PortalAnnouncement = {
  id: string;
  message: string;
  type: string;
  linkUrl?: string | null;
};

const TYPE_LABELS: Record<string, string> = {
  DISCOUNT: 'Offer',
  HEALTH_CAMP: 'Health camp',
  PROMO: 'Promo',
  GENERAL: 'Update',
};

function AnnouncementItem({ item }: { item: PortalAnnouncement }) {
  const label = TYPE_LABELS[item.type] ?? 'Update';
  const content = (
    <>
      <span className="shrink-0 rounded-full bg-amber-400/25 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-100 ring-1 ring-amber-300/40">
        {label}
      </span>
      <span className="text-sm font-medium text-amber-50">{item.message}</span>
    </>
  );

  if (item.linkUrl) {
    return (
      <a
        href={item.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 px-8 hover:underline"
      >
        {content}
      </a>
    );
  }

  return <span className="inline-flex items-center gap-3 px-8">{content}</span>;
}

export default function PatientPortalAnnouncementTicker({
  items,
}: {
  items: PortalAnnouncement[];
}) {
  if (!items.length) return null;

  const loop = [...items, ...items];
  const durationSec = Math.max(18, items.length * 14);

  return (
    <div
      className="relative z-20 border-t border-amber-400/30 bg-gradient-to-r from-amber-600/95 via-amber-500/90 to-amber-600/95 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)]"
      aria-live="polite"
      aria-label="Hospital announcements"
    >
      <div className="flex items-center gap-2 overflow-hidden py-2.5 pl-3 pr-2 sm:pl-4">
        <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-amber-950/25 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-50 ring-1 ring-amber-200/25">
          <Megaphone className="h-3.5 w-3.5" aria-hidden />
          Live
        </span>
        <div className="portal-announcement-mask min-w-0 flex-1 overflow-hidden">
          <div
            className="flex w-max animate-portal-announcement-scroll hover:[animation-play-state:paused]"
            style={{ animationDuration: `${durationSec}s` } as CSSProperties}
          >
            {loop.map((item, i) => (
              <AnnouncementItem key={`${item.id}-${i}`} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
